/* CACB signup timing v1. Anonymous, consent-gated analytics. No email values read. */
(() => {
  'use strict';
  const localStorage = (() => { try { return window.localStorage; } catch (_) { return null; } })();
  const sessionStorage = (() => { try { return window.sessionStorage; } catch (_) { return null; } })();
  const VERSION = 'cacb_signup_v1';
  const KEY = VERSION;
  const SESSION = KEY + '_session';
  const PENDING = KEY + '_pending';
  const ACCEPTED = KEY + '_accepted';
  const isEditor = () => Boolean(window.Shopify && (Shopify.designMode || (Shopify.theme && Shopify.theme.role !== 'main')));
  const consent = () => Boolean(window.Shopify && Shopify.customerPrivacy &&
    typeof Shopify.customerPrivacy.analyticsProcessingAllowed === 'function' &&
    Shopify.customerPrivacy.analyticsProcessingAllowed());
  const read = (storage, key) => { try { return JSON.parse(storage.getItem(key)); } catch (_) { return null; } };
  const write = (storage, key, value) => { try { storage.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; } };
  let cohort = null;
  let session = null;
  let lastTick = performance.now();
  let exposed = false;
  let continued = false;
  let eligibleReported = false;
  const queue = [];
  let queueLoaded = false;
  const QUEUE = KEY + '_queue';
  function loadQueue() {
    if (queueLoaded || !consent() || isEditor()) return;
    queueLoaded = true;
    const saved = read(sessionStorage, QUEUE);
    if (Array.isArray(saved)) queue.push(...saved.filter(item => item && typeof item.name === 'string' && item.name.startsWith(VERSION + '_') && Date.now() - item.at < 30 * 60 * 1000).slice(-100));
  }
  function saveQueue() { if (consent() && !isEditor()) write(sessionStorage, QUEUE, queue.slice(-100)); }
  const testPopups = new WeakSet();
  function currentSession() {
    const saved = read(sessionStorage, SESSION);
    return saved && Number.isFinite(saved.last) && Date.now() - saved.last < 30 * 60 * 1000 ? saved : { ms: 0, exposed: false, continued: false, last: Date.now() };
  }
  function flush() {
    if (!consent() || isEditor()) { queue.length = 0; return; }
    loadQueue();
    if (typeof window.clarity !== 'function') return;
    while (queue.length) {
      const item = queue.shift();
      if (Number.isFinite(item.elapsedSeconds)) window.clarity('set', 'cacb_signup_elapsed_seconds', String(item.elapsedSeconds));
      if (item.arm) window.clarity('set', 'cacb_signup_arm', String(item.arm));
      window.clarity('set', 'cacb_signup_test', VERSION);
      window.clarity('event', item.name);
      saveQueue();
    }
  }
  const event = (name, source, elapsedSeconds) => {
    if (!consent() || isEditor()) return;
    loadQueue();
    queue.push({ at: Date.now(), elapsedSeconds, name: VERSION + '_' + name + (source ? '_' + source : ''), arm: cohort?.arm });
    saveQueue();
    flush();
  };
  function restore() {
    if (!consent() || isEditor()) return;
    const saved = read(localStorage, KEY);
    if (saved && [12, 60].includes(saved.arm)) cohort = saved;
    if (cohort && !session) {
      session = currentSession();
      exposed = session.exposed; continued = session.continued;
    }
  }
  function enroll() {
    if (!consent() || isEditor() || read(localStorage, ACCEPTED)) return false;
    if (!cohort) {
      cohort = read(localStorage, KEY);
      if (!cohort || ![12, 60].includes(cohort.arm)) {
        const random = new Uint32Array(1); crypto.getRandomValues(random);
        cohort = { arm: random[0] % 2 ? 12 : 60 };
        if (!write(localStorage, KEY, cohort)) { cohort = null; return false; }
      }
      session = currentSession();
      if (!write(sessionStorage, SESSION, session)) { cohort = null; return false; }
      exposed = session.exposed; continued = session.continued;
      // Emitted once per page so recorded sessions are filterable; use unique visitors, never event totals.

    }
    if (!session || !write(sessionStorage, SESSION, session)) { cohort = null; return false; }
    if (!eligibleReported) { event('eligible'); eligibleReported = true; }
    return true;
  }
  function tick() {
    flush();
    const now = performance.now(); const delta = Math.min(now - lastTick, 1500); lastTick = now;
    if (cohort && session && consent() && !document.hidden) {
      session.ms += delta; session.last = Date.now(); write(sessionStorage, SESSION, session);
    }
  }
  setInterval(tick, 500);
  const sources = new Set(['popup', 'footer', 'inline']);
  function sourceOf(form) {
    const source = form.closest('[data-cacb-signup-source]')?.dataset.cacbSignupSource;
    return sources.has(source) ? source : null;
  }
  function continuation() {
    if (!cohort || !session || !exposed || continued || !consent()) return;
    continued = true; session.continued = true; write(sessionStorage, SESSION, session); event('shopping_continued');
  }
  window.CACBSignupTest = {
    schedule(popup) {
      let elapsed = 0; let previous = performance.now();
      const timer = setInterval(() => {
        const now = performance.now();
        if (!document.hidden) elapsed += Math.min(now - previous, 1500);
        previous = now;
        const active = enroll();
        // Without analytics consent or storage, retain original timer and collect no experiment data.
        const due = active ? session.ms >= cohort.arm * 1000 : elapsed >= Number(popup.dataset.delay) * 1000;
        if (due && !document.hidden) { clearInterval(timer); if (active) testPopups.add(popup); popup.open(); }
      }, 250);
      return () => clearInterval(timer);
    },
    shown(popup) {
      if (!testPopups.has(popup) || !cohort || !session || !consent() || isEditor() || popup.querySelector('[data-cacb-form-accepted]')) return;
      exposed = true; session.exposed = true; write(sessionStorage, SESSION, session); event('popup_shown');
    },
    dismissed(popup) { if (testPopups.has(popup) && cohort && exposed) event('popup_dismissed'); }
  };
  document.addEventListener('submit', (e) => {
    const form = e.target; const source = sourceOf(form);
    if (!source || isEditor()) return;
    tick();
    const input = form.querySelector('[name="contact[tags]"]');
    if (cohort && consent() && input) {
      const tags = input.value.split(',').map(s => s.trim()).filter(Boolean);
      tags.push(VERSION + '_arm_' + cohort.arm, VERSION + '_source_' + source);
      input.value = [...new Set(tags)].join(',');
    }
    // Store only origin metadata, never the address. Match native response to this exact form.
    if (consent()) write(sessionStorage, PENDING, { id: form.id, source, at: Date.now(), arm: cohort?.arm || null, elapsedSeconds: session ? Math.round(session.ms / 1000) : null });
  }, true);
  let responseHandled = false;
  function checkResponse() {
    if (responseHandled || isEditor()) return;
    restore();
    const pending = consent() ? read(sessionStorage, PENDING) : null;
    if (pending && Date.now() - pending.at < 30 * 60 * 1000 && sources.has(pending.source)) {
      const form = document.getElementById(pending.id);
      if (form?.querySelector('[data-cacb-form-accepted="true"]')) {
        if (consent()) {
          if ([12, 60].includes(pending.arm)) { cohort = { arm: pending.arm }; session = session || { ms: 0, exposed: false, continued: false }; }
          event('form_accepted', pending.source, pending.elapsedSeconds);
          write(localStorage, ACCEPTED, true);
          sessionStorage.removeItem(PENDING);
          responseHandled = true;
        }
      }
    }
  }
  function init() {
    checkResponse();
    // Continued browsing is a product/collection click or successful cart add after an exposure.
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a) return;
      try { const url = new URL(a.href, location.href); if (url.origin === location.origin && /^\/(products|collections)\//.test(url.pathname)) continuation(); } catch (_) {}
    });
    document.addEventListener('on:cart:add', continuation);
  }
  document.addEventListener('visitorConsentCollected', checkResponse);
  if (window.Shopify && typeof Shopify.loadFeatures === 'function' && !Shopify.customerPrivacy) {
    Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => { if (!error) checkResponse(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
